import { useEffect, useCallback } from 'react'

interface UseKeyboardShortcutsOptions {
  onTogglePlay: () => void
  onGenerate?: () => void
  onUndo?: () => void
  onRedo?: () => void
}

/**
 * Hook for keyboard shortcuts
 * Space: Toggle play/pause
 * Enter: Generate (optional)
 * Cmd/Ctrl+Z: Undo (optional)
 * Cmd/Ctrl+Shift+Z: Redo (optional)
 */
export function useKeyboardShortcuts({
  onTogglePlay,
  onGenerate,
  onUndo,
  onRedo,
}: UseKeyboardShortcutsOptions): void {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      const isMod = e.metaKey || e.ctrlKey

      // Undo/Redo work even in inputs (standard behavior)
      if (isMod && e.key === 'z') {
        if (e.shiftKey && onRedo) {
          e.preventDefault()
          onRedo()
          return
        } else if (!e.shiftKey && onUndo) {
          e.preventDefault()
          onUndo()
          return
        }
      }

      // Don't capture other shortcuts when typing in inputs
      if (isInput) return

      switch (e.code) {
        case 'Space':
          e.preventDefault()
          onTogglePlay()
          break
        case 'Enter':
          if (onGenerate) {
            e.preventDefault()
            onGenerate()
          }
          break
      }
    },
    [onTogglePlay, onGenerate, onUndo, onRedo]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}
